import React, { useRef, Suspense } from 'react'
import { useFrame } from '@react-three/fiber'
import { useSpring, animated } from '@react-spring/three'
import { Text } from '@react-three/drei'
import * as THREE from 'three'

// Simple Golden Gate Bridge using basic geometries
const SimpleGoldenGateBridge = ({ position, scale = 1, rotationSpeed = 0.01 }) => {
  const groupRef = useRef()

  // Remove floating animation - landmarks should sit on stage
  // const { positionY } = useSpring({
  //   from: { positionY: position[1] },
  //   to: { positionY: position[1] + 0.8 },
  //   config: { duration: 4000 },
  //   loop: { reverse: true }
  // })

  // Use the same materials as CyberpunkGeometry to avoid conflicts
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color('#ff6600'), // Orange for Golden Gate
    metalness: 0.9,
    roughness: 0.1,
    emissive: new THREE.Color('#ff6600'),
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0.8,
  })

  const wireframeMaterial = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#00ffff'),
    wireframe: true,
    transparent: true,
    opacity: 0.4,
  })

  // Minimal animation for landmarks - just subtle glitch effect, no rotation
  useFrame((state, delta) => {
    if (groupRef.current) {
      // Only subtle glitch effect, no rotation for landmarks
      const time = state.clock.elapsedTime
      const glitchIntensity = Math.sin(time * 10) * 0.001
      groupRef.current.position.x += glitchIntensity
      groupRef.current.position.z += glitchIntensity * 0.5
    }
  })

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
    >
      {/* Bridge towers - taller and more recognizable */}
      <mesh position={[-0.8, 1, 0]} material={material}>
        <boxGeometry args={[0.1, 2, 0.1]}/>
      </mesh>
      <mesh position={[0.8, 1, 0]} material={material}>
        <boxGeometry args={[0.1, 2, 0.1]}/>
      </mesh>

      {/* Bridge deck - positioned correctly */}
      <mesh position={[0, 0.3, 0]} material={material}>
        <boxGeometry args={[1.8, 0.05, 0.2]}/>
      </mesh>

      {/* Suspension cables */}
      <mesh position={[-0.4, 0.8, 0]} rotation={[0, 0, 0.3]} material={material}>
        <boxGeometry args={[0.02, 0.8, 0.02]}/>
      </mesh>
      <mesh position={[0.4, 0.8, 0]} rotation={[0, 0, -0.3]} material={material}>
        <boxGeometry args={[0.02, 0.8, 0.02]}/>
      </mesh>

      {/* 3D Text Signpost positioned directly above Golden Gate Bridge */}
      <Suspense fallback={null}>
        <Text
          position={[0, 3.2, 0]}
          fontSize={0.25}
          color="#00ffff"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.01}
          outlineColor="#000000"
        >
          SAN FRANCISCO
        </Text>
      </Suspense>


      {/* Connection line to landmark */}
      <mesh position={[0, 2.4, 0]} rotation={[0, 0, 0]}>
        <boxGeometry args={[0.01, 1.2, 0.01]}/>
        <meshBasicMaterial
          color="#00ffff"
          transparent
          opacity={0.6}
        />
      </mesh>

    </group>
  )
}

// Simple Burj Khalifa using basic geometries
const SimpleBurjKhalifa = ({ position, scale = 1, rotationSpeed = 0.01 }) => {
  const groupRef = useRef()

  // Use the same materials as CyberpunkGeometry to avoid conflicts
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color('#00ffff'), // Cyan for Burj Khalifa
    metalness: 0.9,
    roughness: 0.1,
    emissive: new THREE.Color('#00ffff'),
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0.8,
  })

  const wireframeMaterial = new THREE.MeshBasicMaterial({
    color: new THREE.Color('#ff00ff'),
    wireframe: true,
    transparent: true,
    opacity: 0.4,
  })

  // Minimal animation for landmarks - just subtle glitch effect, no rotation
  useFrame((state, delta) => {
    if (groupRef.current) {
      // Only subtle glitch effect, no rotation for landmarks
      const time = state.clock.elapsedTime
      const glitchIntensity = Math.sin(time * 10) * 0.001
      groupRef.current.position.x += glitchIntensity
      groupRef.current.position.z += glitchIntensity * 0.5
    }
  })

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
    >
      {/* Main tower - tapered like Burj Khalifa */}
      <mesh position={[0, 1.8, 0]} material={material}>
        <coneGeometry args={[0.3, 3, 8]}/>
      </mesh>

      {/* Middle section */}
      <mesh position={[0, 0.8, 0]} material={material}>
        <coneGeometry args={[0.4, 1, 8]}/>
      </mesh>

      {/* Base */}
      <mesh position={[0, 0.3, 0]} material={material}>
        <cylinderGeometry args={[0.5, 0.6, 0.6]}/>
      </mesh>

      {/* Spire */}
      <mesh position={[0, 3.5, 0]} material={material}>
        <coneGeometry args={[0.05, 0.8, 6]}/>
      </mesh>

      {/* 3D Text Signpost positioned directly above Burj Khalifa */}
      <Suspense fallback={null}>
        <Text
          position={[0, 4.5, 0]}
          fontSize={0.2}
          color="#ff00ff"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.01}
          outlineColor="#000000"
        >
          DUBAI
        </Text>
      </Suspense>

      {/* Connection line to landmark */}
      <mesh position={[0, 2.4, 0]} rotation={[0, 0, 0]}>
        <boxGeometry args={[0.01, 1.2, 0.01]}/>
        <meshBasicMaterial
          color="#00ffff"
          transparent
          opacity={0.6}
        />
      </mesh>

    </group>
  )
}

export { SimpleGoldenGateBridge, SimpleBurjKhalifa }
